import { all, takeLatest, call, put, retry, select } from 'redux-saga/effects';

import { feedActions, feedSelector } from '$store/feed/index';
import { Api } from '$api';
import { mainActions, mainSelector } from '$store/main';
import { postsActions, postsSelector } from '$store/posts';
import {
  FeedItemType,
  HideNotificationAction,
  LoadFeedAction,
  LoadMoreAction,
} from '$store/feed/interface';
import { toastActions } from '$store/toast';
import { batchActions } from '$store';
import { followersActions } from '$store/followers';
import { storiesActions } from '$store/stories';
import { streamsActions } from '$store/streams';
import router from '$navigation/router';
import { liveActions } from '$store/live';
import { UserModel } from '$store/models';

function* loadFeedWorker(action: LoadFeedAction) {
  try {
    const { section: sectionRaw, query } = action.payload;
    const { contentPriority } = yield select(mainSelector);

    let section = sectionRaw;
    if (!section) {
      section = 'suggestions';
    }

    const { params } = yield call(router.getState);
    let feedUserId = 0;
    if (params && params.feedUserId > 0) {
      feedUserId = +params.feedUserId;
    }

    const data = yield retry(Infinity, 3000, Api.post, '/feed', {
      startFrom: 0,
      section:
        contentPriority === 'adult' && section === 'smart' ? 'suggestions' : section,
      suggestions: section === 'suggestions',
      query: (query || '').trim(),
      feedUserId,
    });

    const suggestedUsers: { [index: number]: UserModel } = {};
    if (data.usersSuggestions) {
      for (let user of data.usersSuggestions) {
        suggestedUsers[user.id] = user;
      }
    }

    yield put(
      batchActions(
        followersActions.setFollowing(data.followed || {}),
        mainActions.setUsers(data.users),
        mainActions.setGroups(data.groups),
        postsActions.setPosts(Object.values(data.posts)),
        storiesActions.setStories(data.stories || []),
        liveActions.setStreams(data.streams || []),
        feedActions.setFeed({
          nextFrom: data.nextFrom,
          items: data.feed,
          suggestions: data.suggestions,
          notifications: data.notifications,
          hasNewFollowingPosts: data.hasNewFollowingPosts,
          hasNewSponsorsPosts: data.hasNewSponsorsPosts,
          sources: data.sources,
          welcomeHidden: data.welcomeHidden,
          section,
        }),
        feedActions.setPromotedBattle(data.battlePromoted),
        !!data.usersSuggestions &&
          feedActions.setUsersSuggestions(data.usersSuggestions.map((user) => user.id)),
        mainActions.setUsers(suggestedUsers),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadMoreWorker(action: LoadMoreAction) {
  try {
    const { contentPriority } = yield select(mainSelector);
    const { feed } = yield select(feedSelector);
    const { posts } = yield select(postsSelector);
    const { section, query } = action.payload;
    const { page, nextFrom } = feed[section];

    const { params } = yield call(router.getState);
    let feedUserId = 0;
    if (params && params.feedUserId > 0) {
      feedUserId = +params.feedUserId;
    }

    const renderedModels: { [index: number]: boolean } = {};
    const renderedPosts: number[] = [];
    for (let item of feed[section]?.items || []) {
      if (item.type !== 'post') {
        continue;
      }

      if (!posts[+item.object]) {
        continue;
      }

      const ownerId = posts[+item.object].ownerId;
      if (ownerId > 0) {
        renderedModels[posts[+item.object].ownerId] = true;
      }
      renderedPosts.push(+item.object);
    }

    const data = yield call(Api.post, '/feed', {
      startFrom: +nextFrom,
      section:
        contentPriority === 'adult' && section === 'smart' ? 'suggestions' : section,
      query: (query || '').trim(),
      page,
      renderedAds:
        feed[section]?.items &&
        feed[section]?.items.filter((item) => item.adId > 0).map((item) => item.adId),
      renderedModels: Object.keys(renderedModels).map((key) => +key),
      feedUserId,
      renderedPosts: renderedPosts.slice(-100),
    });
    yield put(
      batchActions(
        followersActions.setFollowing(data.followed || {}),
        mainActions.setUsers(data.users),
        mainActions.setGroups(data.groups),
        postsActions.setPosts(Object.values(data.posts)),
        feedActions.setMoreFeed({
          nextFrom: data.nextFrom,
          items: data.feed,
          hasNewFollowingPosts: data.hasNewFollowingPosts,
          hasNewSponsorsPosts: data.hasNewSponsorsPosts,
          sources: data.sources,
          section,
        }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* hideNotificationWorker(action: HideNotificationAction) {
  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/feed/hide_notify', {
      id: action.payload,
    });
    yield put(feedActions.hideNotificationFromUI(action.payload));
    yield put(toastActions.hideToast());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadCommonFeedWorker() {
  try {
    const data = yield retry(Infinity, 3000, Api.post, '/feed/common', {
      startFrom: 0,
    });
    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        mainActions.setGroups(data.groups),
        postsActions.setPosts(Object.values(data.posts)),
        feedActions.setCommonFeed({
          nextFrom: data.nextFrom,
          items: data.feed,
        }),
      ),
    );
  } catch (e) {
    yield put(feedActions.commonFeedLoadingFailed());
  }
}

function* commonFeedLoadMore() {
  try {
    const { commonNextFrom } = yield select(feedSelector);
    const data = yield retry(Infinity, 3000, Api.post, '/feed/common', {
      startFrom: commonNextFrom,
    });
    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        mainActions.setGroups(data.groups),
        postsActions.setPosts(Object.values(data.posts)),
        feedActions.setCommonFeedMore({
          nextFrom: data.nextFrom,
          items: data.feed,
        }),
      ),
    );
  } catch (e) {
    yield put(feedActions.commonFeedLoadMoreFailed());
  }
}

function* hideWelcomeWorker() {
  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/feed/hide_welcome');
    yield put(batchActions(toastActions.hideToast(), feedActions.setWelcomeHidden()));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* feedSaga() {
  yield all([
    takeLatest(feedActions.loadFeed, loadFeedWorker),
    takeLatest(feedActions.loadMore, loadMoreWorker),
    takeLatest(feedActions.hideNotification, hideNotificationWorker),
    takeLatest(feedActions.loadCommonFeed, loadCommonFeedWorker),
    takeLatest(feedActions.commonFeedLoadMore, commonFeedLoadMore),
    takeLatest(feedActions.hideWelcome, hideWelcomeWorker),
  ]);
}
